import Edit16 from '@carbon/icons-react/es/edit/16';
import Erase16 from '@carbon/icons-react/es/erase/16';
import Locked16 from '@carbon/icons-react/es/locked/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import Unlocked16 from '@carbon/icons-react/es/unlocked/16';
import UserFollow16 from '@carbon/icons-react/es/user--follow/16';
import { Tooltip } from 'antd';
import React, { FC, ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { Menu } from 'components/atoms/dropdown';
import {
  useDefaultNativeTeamsInAccount,
  useNativeTeamsInAccount,
} from 'queries/placeholder';
import { AutoAllocateIcon, CellWrapper, ItemDelete } from './component';
import { useAllocationsMoreActions } from './use-allocations-more-options';
import { useTeamRemovalConfirmation } from './use-team-removal-confirmation';

interface Props {
  projectId: number;
  teamId: number;
  softAllocationsEnabled: boolean;
  onFindMember: () => void;
  onEditTeam: () => void;
  onDeleteAllocations: () => void;
  onConvertAllocations: (toSoft: boolean) => void;
  onTeamRemoval: (projectId: number, teamId: number) => void;
  children: ReactNode;
  autoAllocateProps?: {
    autoAllocation?: boolean;
    showIcon?: boolean;
  };
}

export const TeamWrapperWithActions: FC<Props> = ({
  projectId,
  teamId,
  softAllocationsEnabled,
  onFindMember,
  onEditTeam,
  onDeleteAllocations,
  onConvertAllocations,
  onTeamRemoval,
  children,
  autoAllocateProps,
}) => {
  const { data: teams = [] } = useNativeTeamsInAccount();
  const { data: defaultTeams = [] } = useDefaultNativeTeamsInAccount();

  const team = useMemo(
    () => teams.find(({ teamId: tId }) => tId === teamId),
    [teamId, teams],
  );
  const defaultTeamIds = useMemo(
    () => defaultTeams.map(({ teamId: _teamid }) => _teamid),
    [defaultTeams],
  );
  const isDefaultTeam = useMemo(
    () => defaultTeamIds.includes(teamId),
    [defaultTeamIds, teamId],
  );
  const resourceIdentifier: ResourceIdentifier = useMemo(
    () => ({
      type: ResourceType.Role,
      roleId: team?.roleId ?? -1,
      teamId,
    }),
    [team?.roleId, teamId],
  );

  const { showConfirmation } = useTeamRemovalConfirmation({
    projectId,
    teamId,
    onRemovalConfirm: onTeamRemoval,
  });

  const {
    detailedAllocatedMinutes,
    onMenuVisibleChange,
    showDeleteAllocationsConfirmation,
    showConvertAllocationsConfirmation,
  } = useAllocationsMoreActions({
    resourceName: team?.teamName ?? '',
    projectId,
    resourceIdentifier,
    onDeleteAllocations,
    onConvertAllocations,
  });

  return (
    <CellWrapper
      onMenuVisibleChange={onMenuVisibleChange}
      primaryButtonsProps={[
        ...(autoAllocateProps?.autoAllocation === true &&
        autoAllocateProps?.showIcon === true
          ? [
              {
                icon: <AutoAllocateIcon />,
                tooltip: (
                  <FormattedMessage
                    id="resource_management.auto_allocation.tooltip"
                    values={{
                      b: (chunks) => <strong>{chunks}</strong>,
                      br: () => <br />,
                    }}
                  />
                ),
              },
            ]
          : []),
        {
          icon: <UserFollow16 />,
          tooltip: (
            <FormattedMessage id="resource_management.actions.find_member" />
          ),
          onClick: onFindMember,
        },
      ]}
      menu={
        <Menu>
          <Menu.Header>
            <span>
              <FormattedMessage id="more_actions" />
            </span>
          </Menu.Header>
          <Menu.ItemEdit
            onClick={onEditTeam}
            disabled={isDefaultTeam}
            icon={<Edit16 />}
          >
            <Tooltip
              title={
                isDefaultTeam ? (
                  <FormattedMessage id="resource_management.actions.edit_placeholder.disabled" />
                ) : null
              }
            >
              <span>
                <FormattedMessage id="resource_management.actions.edit_placeholder" />
              </span>
            </Tooltip>
          </Menu.ItemEdit>
          {autoAllocateProps?.autoAllocation === true ? null : (
            <Menu.ItemEdit
              onClick={showDeleteAllocationsConfirmation}
              disabled={detailedAllocatedMinutes.totalAllocatedMinutes === 0}
              icon={<Erase16 />}
            >
              <Tooltip
                title={
                  detailedAllocatedMinutes.totalAllocatedMinutes !==
                  0 ? null : (
                    <FormattedMessage id="resource_management.actions.allocations.delete_all.disabled" />
                  )
                }
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.delete_all" />
                </span>
              </Tooltip>
            </Menu.ItemEdit>
          )}
          {softAllocationsEnabled &&
            detailedAllocatedMinutes.totalAllocatedMinutes !== 0 &&
            autoAllocateProps?.autoAllocation === false &&
            (detailedAllocatedMinutes.softAllocatedMinutes === 0 ? (
              <Menu.ItemEdit
                onClick={() => {
                  showConvertAllocationsConfirmation(
                    detailedAllocatedMinutes.softAllocatedMinutes === 0,
                  );
                }}
                icon={<Unlocked16 />}
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.change_medium.soft" />
                </span>
              </Menu.ItemEdit>
            ) : (
              <Menu.ItemEdit
                onClick={() => {
                  showConvertAllocationsConfirmation(
                    detailedAllocatedMinutes.softAllocatedMinutes === 0,
                  );
                }}
                icon={<Locked16 />}
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.change_medium.hard" />
                </span>
              </Menu.ItemEdit>
            ))}
          <Menu.Divider />
          <ItemDelete onClick={showConfirmation} icon={<TrashCan16 />}>
            <span>
              <FormattedMessage id="resource_management.actions.remove_placeholder_from_project" />
            </span>
          </ItemDelete>
        </Menu>
      }
    >
      {children}
    </CellWrapper>
  );
};
